import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { extractError } from "@/lib/api";
import { Eye, EyeOff } from "lucide-react";

const REMEMBER_KEY = "remember_login";

function loadRemembered() {
  try {
    const raw = localStorage.getItem(REMEMBER_KEY);
    if (!raw) return null;
    const decoded = JSON.parse(atob(raw));
    return decoded && decoded.email ? decoded : null;
  } catch { return null; }
}

function saveRemembered(email, password) {
  try {
    localStorage.setItem(REMEMBER_KEY, btoa(JSON.stringify({ email, password })));
  } catch { /* ignore */ }
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  // Auto-prefill from previous successful login on this device.
  // If credentials exist, auto-submit so the user goes straight to the dashboard
  // (only fires ONCE per session so a failed login doesn't loop).
  useEffect(() => {
    const saved = loadRemembered();
    if (saved) {
      setEmail(saved.email || "");
      setPassword(saved.password || "");
      setHasSaved(true);
      // one-shot auto-login guard
      const alreadyTried = sessionStorage.getItem("auto_login_attempted");
      if (!alreadyTried && saved.email && saved.password) {
        sessionStorage.setItem("auto_login_attempted", "1");
        // Small timeout so the form fields render + user sees the logo briefly
        setTimeout(() => {
          submitCore(saved.email, saved.password);
        }, 400);
      }
    }
    // eslint-disable-next-line
  }, []);

  const submitCore = async (em, pw) => {
    setLoading(true);
    try {
      const user = await login(em, pw);
      saveRemembered(em, pw);
      toast.success("تم تسجيل الدخول بنجاح");
      const roles = user.roles || [];
      let dest = "/dashboard";
      if (roles.some((r) => ["admin", "super_admin"].includes(r))) dest = "/admin";
      else if (roles.includes("moderator")) dest = "/admin/community";
      nav(dest);
    } catch (err) {
      toast.error(extractError(err, "فشل تسجيل الدخول"));
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    await submitCore(email, password);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative">
        <img
          src="/coach-hero-1.png"
          className="absolute inset-0 h-full w-full object-cover object-top"
          alt="المدرب فيصل"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/85 via-primary/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-primary-foreground">
          <h2 className="font-display text-4xl font-semibold mb-4">أهلًا بعودتك</h2>
          <p className="max-w-md opacity-90">سجّل دخولك للوصول إلى دوراتك، وصفاتك، ومحادثاتك الخاصة مع المدرب.</p>
        </div>
      </div>

      <div className="flex items-center justify-center min-h-screen p-4 sm:p-6 lg:p-12">
        <div className="w-full max-w-md sm:max-w-lg space-y-6">
          <div className="text-center lg:text-start">
            <Link to="/" className="inline-flex items-center gap-3 mb-6">
              <img src="/faisalfit-logo.png" alt="FaisalFit" className="h-12 w-12 rounded-xl shadow" />
            </Link>
            <h1 className="text-2xl sm:text-3xl font-display font-semibold mb-2">تسجيل الدخول</h1>
            <p className="text-muted-foreground text-sm sm:text-base">
              {hasSaved ? "بياناتك جاهزة — فقط اضغط دخول." : "أدخل بريدك الإلكتروني وكلمة المرور للمتابعة."}
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form" autoComplete="on">
            <div className="space-y-2">
              <Label htmlFor="email">البريد الإلكتروني</Label>
              <Input
                id="email" type="email" required autoComplete="username" name="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                placeholder="you@example.com"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">كلمة المرور</Label>
                <Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="link-forgot">
                  نسيت كلمة المرور؟
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password" type={showPass ? "text" : "password"} required
                  autoComplete="current-password" name="password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="pe-10"
                />
                <button type="button"
                  onClick={() => setShowPass((s) => !s)}
                  className="absolute inset-y-0 end-2 text-foreground/40 hover:text-foreground/70"
                  aria-label={showPass ? "إخفاء" : "إظهار"}
                  tabIndex={-1}
                  data-testid="toggle-password-visibility">
                  {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="login-submit">
              {loading ? "جارٍ الدخول..." : "دخول"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
